import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type AuthStep = 'email' | 'code' | 'offer-telegram' | 'name'

interface AuthFlowState {
  step: AuthStep
  email: string
  nextResendAt: string | null
  expiredAt: string | null
  attemptsLeft: number | null
  isLocked: boolean
  needsRegistration: boolean
  error: string | null
  debugCode: string | null

  setStep: (step: AuthStep) => void
  setEmail: (email: string) => void
  setCooldown: (next_resend: string, expired_at: string) => void
  setError: (error: string | null) => void
  setAttemptsLeft: (n: number | null) => void
  setLocked: (v: boolean) => void
  setNeedsRegistration: (v: boolean) => void
  setDebugCode: (c: string | null) => void
  reset: () => void
}

const initial = {
  step: 'email' as AuthStep,
  email: '',
  nextResendAt: null,
  expiredAt: null,
  attemptsLeft: null,
  isLocked: false,
  needsRegistration: false,
  error: null,
  debugCode: null
}

/**
 * Zustand-стор состояния флоу web-авторизации.
 * Persist (sessionStorage) только для step+email — чтобы перезагрузка на шаге
 * кода не сбрасывала юзера на email (таймер resend живёт в своём ключе
 * sessionStorage через useResendTimer). Эфемерное UI-состояние (error,
 * attemptsLeft, isLocked, debugCode) не персистится — после reload чистое.
 * @returns Стор шагов/таймеров/ошибок флоу авторизации
 */
export const useAuthFlowStore = create<AuthFlowState>()(
  persist(
    (set) => ({
      ...initial,
      setStep: (step) => set({ step, error: null }),
      setEmail: (email) => set({ email }),
      setCooldown: (next_resend, expired_at) => set({ nextResendAt: next_resend, expiredAt: expired_at }),
      setError: (error) => set({ error }),
      setAttemptsLeft: (n) => set({ attemptsLeft: n }),
      setLocked: (v) => set({ isLocked: v }),
      setNeedsRegistration: (v) => set({ needsRegistration: v }),
      setDebugCode: (c) => set({ debugCode: c }),
      reset: () => set({ ...initial })
    }),
    {
      name: 'pauline:auth:flow',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ step: s.step, email: s.email })
    }
  )
)
